import { useDispatch, useSelector } from 'react-redux'
import { User } from '@pff-consumer/schema'
import { accountSelectors, accountSlice, accountActions } from './account.slice'
import { consumerApi } from '../consumer-api/consumer-api'
import { useAccountStorage } from './account-storage'
import { checkHasCurrentSubscription } from './util/checkHasCurrentSubscription'
import { checkHasExpiredSubscription } from './util/checkHasExpiredSubscription'

export const accountHooks = {
  getIsLoading: () => useSelector(accountSelectors.getLoadingState),
  getToken: () => useSelector(accountSelectors.getToken),
  getUser: () => useSelector(accountSelectors.getUser),
  getError: () => useSelector(accountSelectors.getError),
  getIsPremium: () => useSelector(accountSelectors.getIsPremium),
  getIsExpired: () => useSelector(accountSelectors.getIsExpired),
  getSubscriptionType: () => useSelector(accountSelectors.getSubscriptionType),
  getLoginRedirect: () => useSelector(accountSelectors.getLoginRedirect),
  getIsLoggedIn: () => useSelector(accountSelectors.getIsLoggedIn),
  getIsCreatingAccount: () => useSelector(accountSelectors.getIsCreatingAccount),

  useAccountActions: () => {
    const dispatch = useDispatch()
    const { saveTokenToStorage, removeTokenFromStorage } = useAccountStorage()

    const signOut = async () => {
      await removeTokenFromStorage()
      dispatch(accountActions.deleteToken())
      dispatch(accountActions.updateError({ error: null }))
      dispatch(accountActions.updateUser({ user: null, token: null }))
      dispatch(accountActions.setIsLoggedIn({ isLoggedIn: false }))
      consumerApi.util.resetApiState()
    }

    const setError = (e: any) => {
      dispatch(accountActions.updateLoading({ loading: false }))
      dispatch(accountActions.updateError({ error: e }))
      dispatch(accountActions.setIsLoggedIn({ isLoggedIn: false }))
    }

    const startLoading = () => {
      dispatch(accountSlice.actions.updateLoading({ loading: true }))
    }

    const endLoading = () => {
      dispatch(accountActions.updateLoading({ loading: false }))
    }

    const updateUser = async (user: User, token: string) => {
      const isPremiumUser = checkHasCurrentSubscription(user)
      const isSubscriptionExpired = !isPremiumUser && checkHasExpiredSubscription(user)
      await saveTokenToStorage(user, token, isPremiumUser, isSubscriptionExpired)
      dispatch(accountActions.updateUser({ user, token }))
    }

    const clearError = () => {
      dispatch(accountActions.updateError({ error: null }))
    }

    const setLoggedIn = (loggedIn: boolean) => {
      dispatch(accountActions.setIsLoggedIn({ isLoggedIn: loggedIn }))
    }

    const resetErrors = () => {
      dispatch(accountActions.resetError())
    }

    const setRedirectScreen = (payload: { screen?: string }) => {
      dispatch(accountActions.setRedirectScreen(payload))
    }

    const setCreatingAccount = (payload: boolean) => {
      dispatch(accountActions.setIsCreatingAccount({ isCreatingAccount: payload }))
    }

    return {
      signOut,
      clearError,
      resetErrors,
      setRedirectScreen,
      startLoading,
      endLoading,
      updateUser,
      setError,
      setLoggedIn,
      setCreatingAccount,
    }
  },
}
